import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";
import { Spinner } from "@material-tailwind/react";
import { generaleTableApi } from "api/dashboard";
import CAChart from "./components/CAChart";
import { getCookie } from "utils/cookies";
import { getOneOperatorApi } from "api/operator";
import { useParams } from "react-router-dom";
import CAByCategoryChart from "./components/CAByCategoryChart";
import CAProductsTop10Chart from "./components/CAProductsTop10Chart";
import CATcpChart from "./components/CATcpChart";

const ChiffreAffaire = () => {
  const { userID } = useParams();
  const [accessList, setAccessList] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [yearlyData, setYearlyData] = useState([]);
  const [categoryTotalsByYear, setCategoryTotalsByYear] = useState({});
  const [productTotalsByYear, setProductTotalsByYear] = useState({});
  const [tcpTotalsByYear, setTcpTotalsByYear] = useState({});
  const [topProducts, setTopProducts] = useState([]);
  const [showCAData, setShowCAData] = useState(false);

  const lastFiveYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear];
  }, []);

  const aggregateYearTotals = (tableData) => {
    if (!tableData?.data) return { qty: 0, amt: 0 };
    let totalQty = 0;
    let totalAmt = 0;
    Object.entries(tableData.data).forEach(([tcp, products]) => {
      Object.entries(products).forEach(([name, entries]) => {
        const last = entries[entries.length - 1] || {};
        totalQty += parseFloat(last?.sumOfQuantity) || 0;
        totalAmt += parseFloat(last?.sumOfAmount) || 0;
      });
    });
    return { qty: totalQty, amt: totalAmt };
  };

  const aggregateYearCategoryTotals = (tableData) => {
    const map = {};
    if (!tableData?.data) return map;
    Object.entries(tableData.data).forEach(([tcp, products]) => {
      Object.entries(products).forEach(([name, entries]) => {
        const last = entries[entries.length - 1] || {};
        const category = entries?.[0]?.Category || "Autre";
        const amt = parseFloat(last?.sumOfAmount) || 0;
        map[category] = (map[category] || 0) + amt;
      });
    });
    return map;
  };

  const aggregateYearProductTotals = (tableData) => {
    const map = {};
    if (!tableData?.data) return map;
    Object.entries(tableData.data).forEach(([tcp, products]) => {
      Object.entries(products).forEach(([productName, entries]) => {
        const last = entries[entries.length - 1] || {};
        const amt = parseFloat(last?.sumOfAmount) || 0;
        map[productName] = (map[productName] || 0) + amt;
      });
    });
    return map; // Record<productName, amount>
  };

  const aggregateYearTcpTotals = (tableData) => {
    const map = {};
    if (!tableData?.data) return map;
    Object.entries(tableData.data).forEach(([tcp, products]) => {
      let sum = 0;
      Object.entries(products).forEach(([name, entries]) => {
        const last = entries[entries.length - 1] || {};
        sum += parseFloat(last?.sumOfAmount) || 0;
      });
      map[tcp] = sum;
    });
    return map; // Record<tcpName, amount>
  };

  const fetchYear = async (year) => {
    try {
      const fromDate = `${year}-01-01`;
      const toDate = `${year}-12-31`;
      const res = await generaleTableApi(fromDate, toDate);
      const { qty, amt } = aggregateYearTotals(res);
      const categoryMap = aggregateYearCategoryTotals(res);
      const productMap = aggregateYearProductTotals(res);
      const tcpMap = aggregateYearTcpTotals(res);
      return { year, sumOfQuantity: qty, sumOfAmount: amt, categoryMap, productMap, tcpMap };
    } catch (e) {
      return { year, sumOfQuantity: 0, sumOfAmount: 0, categoryMap: {}, productMap: {}, tcpMap: {} };
    }
  };

  const getAccessList = async () => {
    try {
      const id = getCookie("3F2Mb2pJ1YRro5Z4");
      const adminRole = getCookie("lv9u765qzk1gr4fn1e");

      if (adminRole === "FALSE") {
        const response = await getOneOperatorApi(id);
        const accessList = response.userFromRole.access;

        const selectedAccessDetails = accessList.find(
          (el) => el.value._id == userID
        );

        const accessObject = {
          Add: false,
          Read: false,
          Update: false,
          Delete: false,
          ChiffreAffaire: false,
          ChiffreAffaireParCategorie: false,
          Top10Produits: false,
          ChiffreAffaireParTCP: false
        };

        selectedAccessDetails.sectionItems.map((el) => {
          if (el.value == "Add" && el.checked) {
            accessObject.Add = true;
          }
          if (el.value == "Read" && el.checked) {
            accessObject.Read = true;
          }
          if (el.value == "Update" && el.checked) {
            accessObject.Update = true;
          }
          if (el.value == "Delete" && el.checked) {
            accessObject.Delete = true;
          }

          if (el.value == "ChiffreAffaire" && el.checked) {
            accessObject.ChiffreAffaire = true;
          }
          if (el.value == "ChiffreAffaireParCategorie" && el.checked) {
            accessObject.ChiffreAffaireParCategorie = true;
          }
          if (el.value == "Top10Produits" && el.checked) {
            accessObject.Top10Produits = true;
          }
          if (el.value == "ChiffreAffaireParTCP" && el.checked) {
            accessObject.ChiffreAffaireParTCP = true;
          }
        });

        setAccessList({
          Add: accessObject.Add,
          Read: accessObject.Read,
          Update: accessObject.Update,
          Delete: accessObject.Delete,
          ChiffreAffaire: accessObject.ChiffreAffaire,
          ChiffreAffaireParCategorie: accessObject.ChiffreAffaireParCategorie,
          Top10Produits: accessObject.Top10Produits,
          ChiffreAffaireParTCP: accessObject.ChiffreAffaireParTCP,
        });

        if (accessObject.ChiffreAffaire) {
          setShowCAData(true);
        }
      } else if (adminRole === "TRUE") {
        setAccessList({
          Add: true,
          Read: true,
          Update: true,
          Delete: true,
          ChiffreAffaire: true,
          ChiffreAffaireParCategorie: true,
          Top10Produits: true,
          ChiffreAffaireParTCP: true
        });
        setShowCAData(true);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);
      const results = await Promise.all(lastFiveYears.map((y) => fetchYear(y)));
      setYearlyData(results.map((r) => ({ year: r.year, sumOfQuantity: r.sumOfQuantity, sumOfAmount: r.sumOfAmount })));

      const catTotalsByYear = {};
      const prodTotalsByYear = {};
      const tcpTotalsByY = {};
      const globalProductTotals = {};
      results.forEach((r) => {
        catTotalsByYear[r.year] = r.categoryMap || {};
        prodTotalsByYear[r.year] = r.productMap || {};
        tcpTotalsByY[r.year] = r.tcpMap || {};
        Object.entries(r.productMap || {}).forEach(([productName, amt]) => {
          globalProductTotals[productName] = (globalProductTotals[productName] || 0) + Number(amt || 0);
        });
      });
      setCategoryTotalsByYear(catTotalsByYear);
      setProductTotalsByYear(prodTotalsByYear);
      setTcpTotalsByYear(tcpTotalsByY);

      const top10 = Object.entries(globalProductTotals)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([name]) => name);
      setTopProducts(top10);
    } catch (err) {
      setYearlyData([]);
      setCategoryTotalsByYear({});
      setProductTotalsByYear({});
      setTcpTotalsByYear({});
      setTopProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getAccessList();
  }, []);

  useEffect(() => {
    if (showCAData) {
      loadData();
    }
  }, [showCAData]);

  return (
    <Card extra={"w-full pb-10 p-4 h-full"}>
      <header className="relative mb-4 flex items-center justify-between">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Chiffre d'affaire
        </div>
      </header>

      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner className="h-12 w-12" />
        </div>
      ) : (
        <>
          <div className="mt-2 mb-6">
            {accessList.ChiffreAffaire ? <CAChart yearlyData={yearlyData} /> : <></>}
          </div>

          {accessList.ChiffreAffaireParCategorie ? (
            <div className="mt-8">
              <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">Chiffre d'affaire par Catégorie</div>
              <CAByCategoryChart years={lastFiveYears} categoryTotalsByYear={categoryTotalsByYear} />
            </div>
          ) : (
            <></>
          )}

          {accessList.Top10Produits && topProducts.length > 0 ? (
            <div className="mt-8">
              <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">Top 10 Produits (Montant HT) - 5 dernières années</div>
              <CAProductsTop10Chart years={lastFiveYears} topProducts={topProducts} productTotalsByYear={productTotalsByYear} />
            </div>
          ) : (
            <></>
          )}

          {accessList.ChiffreAffaireParTCP ? (
            <div className="mt-8">
              <div className="text-lg font-bold text-navy-700 dark:text-white mb-2">Chiffre d'affaire par TCP</div>
              <CATcpChart years={lastFiveYears} tcpTotalsByYear={tcpTotalsByYear} />
            </div>
          ) : (
            <></>
          )}

          {/* Existing table */}
          {accessList.ChiffreAffaire ? (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="border-b border-gray-200 pb-[10px] text-start text-xs tracking-wide text-gray-600 dark:!border-navy-700">
                      Année
                    </th>
                    <th className="border-b border-gray-200 pb-[10px] text-start text-xs tracking-wide text-gray-600 dark:!border-navy-700">
                      Quantité totale
                    </th>
                    <th className="border-b border-gray-200 pb-[10px] text-start text-xs tracking-wide text-gray-600 dark:!border-navy-700">
                      Montant total (DH)
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyData.map((row) => (
                    <tr key={row.year}>
                      <td className="py-2 text-sm font-medium text-navy-700 dark:text-white">{row.year}</td>
                      <td className="py-2 text-sm font-bold text-navy-700 dark:text-white">
                        {new Intl.NumberFormat("en-EN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.sumOfQuantity || 0)}
                      </td>
                      <td className="py-2 text-sm font-bold text-navy-700 dark:text-white">
                        {new Intl.NumberFormat("en-EN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.sumOfAmount || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <></>
          )}
        </>
      )}
    </Card>
  );
};

export default ChiffreAffaire;
